import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react'

const prefix = '/cdn-cgi/image/'
/** Only public Dihaw raster assets may use the public transformation endpoint. */
export function imageSources(src: string) {
  try {
    const url = new URL(src)
    if (url.protocol !== 'https:' || url.hostname !== 'cdn.dihaw.ai') return { original: src, thumbnail: '' }
    let path = url.pathname
    if (path.startsWith(prefix)) path = path.slice(path.indexOf('/', prefix.length))
    if (!/^\/(?!\/)/.test(path) || !/\.(png|jpe?g|webp|avif)$/i.test(path)) return { original: src, thumbnail: '' }
    const original = new URL(path, url.origin)
    original.search = url.search
    const thumbnail = new URL(prefix + 'width=80,height=80,fit=scale-down,quality=25,format=webp' + path, url.origin)
    thumbnail.search = url.search
    return { original: original.href, thumbnail: thumbnail.href }
  } catch { return { original: src, thumbnail: '' } }
}

type Props = ImgHTMLAttributes<HTMLImageElement> & { previewSrc?: string }
/** A single DOM image preserves existing gallery sizing, selectors and accessibility. */
export function ProgressiveImage(props: Props) {
  return <ImageStage key={props.src} {...props} />
}
function ImageStage({ src = '', previewSrc, loading = 'lazy', style, onLoad, onError, ...props }: Props) {
  const sources = imageSources(src)
  const thumbnail = props.srcSet ? '' : (previewSrc ?? sources.thumbnail)
  const progressive = Boolean(src && thumbnail && thumbnail !== src)
  const [display, setDisplay] = useState(progressive ? thumbnail : src)
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>(progressive ? 'loading' : 'ready')
  const ref = useRef<HTMLImageElement>(null)
  const thumbnailFailed = useRef(false)
  useEffect(() => {
    if (!progressive) return
    let cancelled = false
    let current: HTMLImageElement | undefined
    let started = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const load = (url: string, fallback: boolean) => {
      const image = new Image()
      current = image
      image.decoding = 'async'
      if (props.crossOrigin) image.crossOrigin = props.crossOrigin
      if (props.referrerPolicy) image.referrerPolicy = props.referrerPolicy
      if (props.fetchPriority) image.fetchPriority = props.fetchPriority
      image.onload = async () => {
        try { await image.decode() } catch { /* onload already confirms a decoded image on older browsers. */ }
        if (cancelled || current !== image || !image.naturalWidth) return
        clearTimeout(timer)
        setDisplay(url)
        setPhase('ready')
      }
      image.onerror = () => {
        if (cancelled || current !== image) return
        clearTimeout(timer)
        if (fallback && sources.original !== url) load(sources.original, false)
        else {
          // Surface a real DOM error event to the caller, then keep the small preview.
          setDisplay(url)
          setPhase('error')
        }
      }
      image.src = url
      timer = setTimeout(() => image.onerror?.(new Event('error')), 20000)
    }
    const start = () => { if (!started) { started = true; load(src, true) } }
    let observer: IntersectionObserver | undefined
    if (loading === 'lazy' && typeof IntersectionObserver !== 'undefined' && ref.current) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer?.disconnect(); start() }
      }, { rootMargin: '300px' })
      observer.observe(ref.current)
    } else start()
    return () => {
      cancelled = true
      clearTimeout(timer)
      observer?.disconnect()
      if (current) { current.onload = null; current.onerror = null; current.src = '' }
    }
  }, [src, thumbnail, progressive, loading, sources.original, props.crossOrigin, props.referrerPolicy, props.fetchPriority])
  return <img {...props} ref={ref} src={display || undefined} loading={loading} decoding={props.decoding || 'async'}
    data-image-state={phase}
    style={{ ...style, ...(progressive && phase === 'loading' ? { filter: 'blur(8px)' } : {}) }}
    onLoad={event => { if (!progressive || phase === 'ready') onLoad?.(event) }}
    onError={event => {
      if (progressive && phase === 'loading') {
        thumbnailFailed.current = true
        // The full-size request is independent; a failed thumbnail never blocks it.
        setDisplay('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"/%3E')
      } else {
        if (progressive && !thumbnailFailed.current) setDisplay(thumbnail)
        onError?.(event)
      }
    }}
  />
}
